/* eslint-disable no-else-return */
import { convertFromRaw, EditorState } from 'draft-js';
import { Map } from 'immutable';
import React from 'react';
import MultiDecorator from 'draft-js-multidecorators';
import Prism from 'prismjs';
import PrismDecorator from 'draft-js-prism';
import compositeDecorator from '../decorators/compositeDecorator';
import { BLOCK_TYPE, ENTITY_TYPE } from './CONSTANTS';
import cleanHTML from './cleanHTML';
import convertFromHtml from './convertHtmlToDraft';
import editorStateToRawState from './editorStateToRawContent';
import removeFirstLineFromHtml from './removeFirstLineFromHtml';

const createEmptyEditorState = () => contentToEditorState({
    blocks: [{
        text: '',
    }],
    entityMap: {},
});

const createDecorator = readOnly => new MultiDecorator([
    compositeDecorator(readOnly),
    new PrismDecorator({
        defaultSyntax: 'javascript',
        prism: Prism,
        getSyntax: (block) => {
            const data = block.getData();
            const language = data.get('language') || data.get('syntax');

            if (typeof Prism.languages[language] === 'object') {
                return language;
            }
            return null;
        },
        render: ({ type, children }) => <span className={`prism-token token ${type}`}>{children}</span>,
    }),
]);

const contentToEditorState = (_rawContent, readOnly = false) => {
    const rawContent = {
        blocks: [..._rawContent.blocks],
        entityMap: { ..._rawContent.entityMap },
    };

    const decorator = createDecorator(readOnly);

    rawContent.blocks = rawContent.blocks.map((block) => {
        if (block.type != null
            && block.type.toLowerCase() === BLOCK_TYPE.ATOMIC
            && block.data != null
            && block.data.rows != null
        ) {
            return {
                ...block,
                data: Map({
                    ...block.data,
                    rows: block.data.rows.map(
                        row => row.map(cell => EditorState.createWithContent(
                            convertFromRaw(cell != null ? cell : { blocks: [{ text: '' }], entityMap: {} }),
                            decorator,
                        )),
                    ),
                }),
            };
        } else if (
            block.type != null
            && block.type.toLowerCase() === BLOCK_TYPE.ATOMIC
            && block.entityRanges != null
            && Array.isArray(block.entityRanges)
            && block.entityRanges.length > 0
        ) {
            const firstEntityRange = block.entityRanges[0];

            if (firstEntityRange != null && firstEntityRange.key != null) {
                const firstEntity = rawContent.entityMap[firstEntityRange.key];

                if (firstEntity != null && firstEntity.type != null && firstEntity.type.toUpperCase() === ENTITY_TYPE.TABLE) {
                    if (block.data == null) {
                        block.data = {};
                    }
                    if (block.data.rows == null) {
                        block.data.rows = [];
                    }

                    block.data.rows = firstEntity.data.rows.map(
                        row => row.map(cell => EditorState.createWithContent(
                            convertFromRaw(cell != null ? cell : { blocks: [{ text: '' }], entityMap: {} }),
                            decorator,
                        )),
                    );

                    delete rawContent.entityMap[firstEntityRange.key];
                }
            }
        }

        return block;
    });

    return EditorState.createWithContent(convertFromRaw(rawContent), decorator);
};

const draftToContentState = (draft, readOnly = true) => {
    if (!draft || (!draft.editor_state && !draft.body)) return null;
    const { editor_state: rawState, body } = draft;
    let contentState;
    if (body && !rawState) {
        const editorState = contentToEditorState(convertFromHtml(cleanHTML(body)), readOnly);
        return editorStateToRawState(editorState);
    }
    return JSON.parse(rawState);
};

const draftToEditorState = (draft, readOnly = true) => {
    const contentState = draftToContentState(draft);

    if (draft && !contentState) {
        return EditorState.createEmpty(createDecorator(readOnly));
    }

    return contentState && contentToEditorState(contentState, readOnly);
};

const questionToEditorState = (question, readOnly = false) => {
    if (!question || !(question.details_content_state || question.details_html)) {
        return createEmptyEditorState();
    }

    if (question.details_content_state) {
        return contentToEditorState(JSON.parse(question.details_content_state));
    }

    return contentToEditorState(convertFromHtml(cleanHTML(question.details_html)));
};

const commentHtmlToEditorState = (comment, readOnly = true) => {

    if (!comment) return null;

    return contentToEditorState(convertFromHtml(cleanHTML(comment)), readOnly);
};

const categoryToEditorState = (category, readOnly = false) => {
    if (!category || !(category.description_content_state || category.description)) {
        return createEmptyEditorState();
    }

    if (category.description_content_state) {
        return contentToEditorState(JSON.parse(category.description_content_state));
    }

    return contentToEditorState(convertFromHtml(cleanHTML(category.description)));
};

const pageToContentState = (page, readOnly = true) => {
    if (!page || (!page.editor_state && !page.html)) return null;
    const { editor_state: rawState, html } = page;
    if (html && !rawState) { // in case of no editor_state, html converted to Editor state returns a blank line which will be removed
        const editorState = contentToEditorState(convertFromHtml(cleanHTML(html)), readOnly);
        const contentState = editorStateToRawState(editorState);
        return removeFirstLineFromHtml(contentState);
    }
    return JSON.parse(rawState);
};

const pageToEditorState = (page, readOnly = true) => {
    const contentState = pageToContentState(page);

    return contentState && contentToEditorState(contentState, readOnly);
};

const textToEditorState = (text) => {
    if (!text) {
        return createEmptyEditorState();
    }

    return contentToEditorState(convertFromHtml(cleanHTML(text)));
};

const teamPreferencesToEditorState = (teamPreferences, readOnly = false) => {
    if (!teamPreferences || !(teamPreferences.description_content_state || teamPreferences.description)) {
        return createEmptyEditorState();
    }

    if (teamPreferences.description_content_state) {
        return contentToEditorState(JSON.parse(teamPreferences.description_content_state), readOnly);
    }

    return contentToEditorState(convertFromHtml(cleanHTML(teamPreferences.description)));
};

export {
    draftToEditorState,
    pageToEditorState,
    questionToEditorState,
    categoryToEditorState,
    pageToContentState,
    textToEditorState,
    teamPreferencesToEditorState,
    commentHtmlToEditorState,
};

export default contentToEditorState;
